import { getReadStatuses } from './message';
import moment from 'moment';

function toMillis(dateLike) {
  if (!dateLike) {
    return -Infinity;
  }
  if (dateLike instanceof Date) {
    return dateLike.getTime();
  }
  if (typeof dateLike === 'number') {
    return dateLike;
  }
  // Assume string or other parseable input
  const ms = new Date(dateLike).getTime();
  return Number.isNaN(ms) ? -Infinity : ms;
}

function resolveFolderName(metadata) {
  return metadata?.folderFullName
    || metadata?.folder
    || metadata?.folderPath
    || metadata?.path;
}

function resolveExternalId(metadata) {
  const id = metadata?.idExternal !== null ? metadata.idExternal : metadata?.id;
  return typeof id === 'undefined' || id === null ? null : Number(id);
}

export function buildFoldersPayload(items) {
  const foldersMap = {};
  items.forEach(item => {
    const folderName = resolveFolderName(item?.metadata || {});
    const idExternal = resolveExternalId(item?.metadata || {});
    if (!folderName || idExternal === null) {
      return;
    }
    if (!foldersMap[folderName]) {
      foldersMap[folderName] = new Set();
    }
    foldersMap[folderName].add(idExternal);
  });
  return Object.keys(foldersMap).map(folder => ({ folder, ids: Array.from(foldersMap[folder]) }));
}

export async function enrichItemsWithReadStatuses(dispatch, items, userId, email, lexonToken) {
  if (!Array.isArray(items) || items.length === 0) {
    return items || [];
  }
  const foldersPayload = buildFoldersPayload(items);
  if (!foldersPayload || foldersPayload.length === 0) {
    return items;
  }
  const statuses = await getReadStatuses(dispatch, userId, email, foldersPayload, lexonToken);
  if (!Array.isArray(statuses) || statuses.length === 0) {
    return items;
  }
  const statusesByFolder = statuses.reduce((folderStatusesAccumulator, status) => {
    const folderNameKey = status.folder;
    const idExternalKey = status.idExternal;
    if (!folderNameKey && folderNameKey !== '') {
      return folderStatusesAccumulator;
    }
    folderStatusesAccumulator[folderNameKey] = folderStatusesAccumulator[folderNameKey] || {};
    const entry = { isRead: !!status.isRead };
    if (typeof status.date !== 'undefined' && status.date !== null) {
      entry.date = status.date;
    }
    folderStatusesAccumulator[folderNameKey][idExternalKey] = entry;
    return folderStatusesAccumulator;
  }, {});

  const enriched = items.map(message => {
    const folderName = resolveFolderName(message?.metadata || {});
    const idExternal = resolveExternalId(message?.metadata || {});
    const statusEntry = (folderName && idExternal !== null && statusesByFolder[folderName])
      ? statusesByFolder[folderName][idExternal]
      : null;

    const hasStatusEntry = (statusEntry !== null && typeof statusEntry !== 'undefined');
    const seen = hasStatusEntry
      ? !!statusEntry.isRead
      : message?.metadata?.seen;

    const overrideDate = (hasStatusEntry && typeof statusEntry.date !== 'undefined' && statusEntry.date !== null)
      ? statusEntry.date
      : null;

    const newMetadata = { ...message.metadata, seen };
    if (overrideDate !== null) {
      // Convert UTC/offset date to a local ISO string with timezone offset
      let localIso;
      if (typeof overrideDate === 'string') {
        const m = window.moment
          ? window.moment.parseZone(overrideDate).local()
          : moment.parseZone(overrideDate).local();
        localIso = m.isValid() ? m.format() : null;
      } else {
        const m = window.moment
          ? window.moment(overrideDate).local()
          : moment(overrideDate).local();
        localIso = m.isValid() ? m.format() : null;
      }
      newMetadata.date = localIso || overrideDate;
    }
    return { ...message, metadata: newMetadata };
  });

  // Sort by final effective date (metadata.date) descending
  enriched.sort((a, b) => {
    const da = toMillis(a?.metadata?.date);
    const db = toMillis(b?.metadata?.date);
    if (da === db) {
      return 0;
    }
    return db - da;
  });

  return enriched;
}

